/**
 * Boot.
 *
 * The order matters and is deliberate:
 *
 * 1. Styles, so the first painted frame is the instrument rather than unstyled text.
 * 2. Storage and settings, because which screen to show depends on them.
 * 3. The shell, mounted with the router already pointed at the launch URL — a deep link
 *    must land on its screen directly, not flash Home on the way there.
 * 4. The service worker, *after* the first frame, so registration never competes with
 *    launch for the main thread.
 *
 * What deliberately does not happen at boot: the microphone is never touched, and the
 * `AudioContext` is created but never resumed. Both need a gesture, and asking for either
 * before the user has done anything is how an audio app loses trust on its first run.
 */

import './ui/styles/tokens.css';
import './ui/styles/components.css';
import './ui/styles/screens.css';

import { createLogger, tag } from './core/log';
import { createShell, registerScreen } from './app/shell';
import { APP_SCOPE, launchRoute, navigate, start as startRouter } from './app/router';
import {
  bootstrap,
  createProjectFromTemplate,
  adoptProject,
  engine,
  openProject,
  project,
  projects,
  pushBanner,
  reportFailure,
  settings,
  recorder,
  saveNow,
  STARTER_TEMPLATES,
  updateSettings,
} from './app/state';
import { effect } from './core/signal';
import { homeScreen } from './ui/screens/home';
import { boardScreen } from './ui/screens/board';
import { libraryScreen } from './ui/screens/library';
import { waveEditorScreen } from './ui/screens/wave-editor';
import { soundLabScreen } from './ui/screens/sound-lab';
import { voiceLabScreen } from './ui/screens/voice-lab';
import { sequencerScreen } from './ui/screens/sequencer';
import { mixerScreen } from './ui/screens/mixer';
import { settingsScreen } from './ui/screens/settings';
import { exportScreen } from './ui/screens/export';
import { onboardingScreen } from './ui/screens/onboarding';
import { ensureLibraryInstalled } from './app/library-install';
import { launchIntent, clearLaunchQuery, installedOnboardingPending } from './platform/standalone';
import { takeSharedPayload } from './platform/share';
import { importDroppedFiles, importProjectBackup } from './app/import';
import { configureMediaSession, setNowPlaying, setTransportState } from './platform/media-session';
import { sequencer } from './app/state';

const log = createLogger('boot');

registerScreen('home', () => homeScreen());
registerScreen('board', () => boardScreen());
registerScreen('library', (context) => libraryScreen(context));
registerScreen('pack', (context) => libraryScreen(context));
registerScreen('wave', (context) => waveEditorScreen(context));
registerScreen('sound-lab', () => soundLabScreen());
registerScreen('voice-lab', () => voiceLabScreen());
registerScreen('sequencer', () => sequencerScreen());
registerScreen('mixer', () => mixerScreen());
registerScreen('settings', () => settingsScreen());
registerScreen('export', () => exportScreen());
registerScreen('onboarding', () => onboardingScreen());

/**
 * The very first touch anywhere resumes the audio context. WebKit only accepts a gesture,
 * and a user who taps a pad expecting sound should get sound on that tap — not on the one
 * after it.
 */
function armAudioUnlock(): void {
  const unlock = (): void => {
    void engine.unlock().then((ok) => {
      if (ok) {
        window.removeEventListener('pointerdown', unlock, true);
        window.removeEventListener('keydown', unlock, true);
      }
    });
  };
  window.addEventListener('pointerdown', unlock, { capture: true });
  window.addEventListener('keydown', unlock, { capture: true });
}

/**
 * A fresh install with nothing in it is a dead end. One generated starter board makes the
 * app playable on first launch with no network and no imports, and it is licence-clean
 * because every sound in it is synthesised on this device.
 */
async function ensureStarterContent(): Promise<void> {
  if (projects.peek().length > 0) return;
  if (settings.peek().starterContentVersion >= 1) return;
  try {
    const template = STARTER_TEMPLATES.find((entry) => entry.id === 'beat-kit') ?? STARTER_TEMPLATES[0]!;
    const created = await createProjectFromTemplate(template, 'First Board');
    await adoptProject(created);
    updateSettings({ starterContentVersion: 1 });
    log.info('Starter content generated', { pads: created.assets.length });
  } catch (error) {
    // A failure here must not stop the app: the user can still start a template by hand.
    log.error('Starter content generation failed', error);
    pushBanner({
      kind: 'warning',
      title: 'Starter sounds could not be generated',
      message: 'PadLab started with an empty board instead. Pick a template on the Home screen to try again.',
      timeout: 8000,
    });
  }
}

/**
 * Runs `task` once the browser is not busy. `requestIdleCallback` where it exists; a timeout
 * on Safari, which still does not have it — a second is long enough for the launch frame and
 * the first interaction to be over.
 */
/**
 * Imports whatever the OS shared into PadLab, then routes to the Library so the user can
 * see it. Failures are reported rather than swallowed — a share that vanishes silently is
 * indistinguishable from an app that ignored it.
 */
async function collectSharedFiles(token: string): Promise<void> {
  try {
    const payload = await takeSharedPayload(token);
    if (payload === null || payload.files.length === 0) return;
    const outcome = await importDroppedFiles(payload.files, 'share');
    if (outcome.imported.length === 0) return;
    await navigate({ name: 'library', projectID: project.peek()?.id }, { replace: true });
  } catch (error) {
    reportFailure('The shared file could not be imported', error);
  }
}

/** Sends a manifest shortcut where it says it goes. */
async function followLaunchShortcut(screen: string | null): Promise<void> {
  if (screen === null) return;
  const projectID = project.peek()?.id;
  switch (screen) {
    case 'new-project':
      await navigate({ name: 'home' }, { replace: true });
      return;
    case 'voice-lab':
      if (projectID !== undefined) await navigate({ name: 'voice-lab', projectID }, { replace: true });
      return;
    case 'sequencer':
      if (projectID !== undefined) await navigate({ name: 'sequencer', projectID }, { replace: true });
      return;
    case 'library':
      if (projectID !== undefined) await navigate({ name: 'library', projectID }, { replace: true });
      return;
    case 'settings':
      await navigate({ name: 'settings', projectID }, { replace: true });
      return;
    default:
      return;
  }
}

function whenIdle(task: () => void): void {
  const idle = (globalThis as { requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number })
    .requestIdleCallback;
  if (typeof idle === 'function') idle(task, { timeout: 3000 });
  else setTimeout(task, 1000);
}

/**
 * Tells the worker whether a take is in flight.
 *
 * The worker has a `recordingActive` guard that refuses `SKIP_WAITING` mid-recording, and
 * nothing ever armed it: the page never sent the message, so the guard defended against
 * nothing. This is the other half of it.
 */
function reportRecordingState(): void {
  if (!('serviceWorker' in navigator)) return;
  effect(() => {
    const active = recorder.isCapturing.value || recorder.hasOpenTake.value;
    navigator.serviceWorker.controller?.postMessage({ type: 'RECORDING_STATE', active });
  });
}

/** Registers the service worker and surfaces an update rather than applying one silently. */
function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  // Classic script, deliberately: module service workers only landed in Safari 16.4 and
  // nothing here needs them. The URL is built from the app scope rather than written
  // relative: on a deep link like `/p/<id>` a relative `./sw.js` resolves to `/p/sw.js`,
  // which the SPA fallback answers with HTML and the browser rejects.
  void navigator.serviceWorker
    .register(`${APP_SCOPE}sw.js`, { scope: APP_SCOPE })
    .then((registration) => {
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (installing === null) return;
        installing.addEventListener('statechange', () => {
          if (installing.state !== 'installed' || navigator.serviceWorker.controller === null) return;
          // Never swap code out from under a recording. The user decides when.
          pushBanner({
            kind: 'info',
            title: 'A new version of PadLab is ready',
            message: 'It installs when you reload. Finish anything you are recording first. Nothing is saved or lost by waiting.',
            actionLabel: 'Reload now',
            action: () => void applyUpdate(installing),
            timeout: 0,
          });
        });
      });
    })
    .catch((error: unknown) => {
      // A missing service worker costs offline launch, nothing else. It is not worth a
      // banner: the capability matrix already reports it honestly.
      log.warn('Service worker registration failed', { secure: isSecureContext, reason: tag(error instanceof Error ? error.name : 'unknown') });
    });
}

/**
 * Applies a waiting update, in the order that actually works.
 *
 * The old code posted `{ type: 'skip-waiting' }` and reloaded immediately. The worker's
 * switch is on `'SKIP_WAITING'`, so the message fell through to `default: return` — the
 * waiting worker was never activated and the reload simply reloaded the old app, for ever.
 * Now: refuse while a take is open, ask, wait for the activation (or a short timeout, so a
 * worker that never answers cannot strand the button), and only then reload.
 */
async function applyUpdate(waiting: ServiceWorker): Promise<void> {
  if (recorder.isCapturing.peek() || recorder.hasOpenTake.peek()) {
    pushBanner({
      kind: 'warning',
      title: 'Not while you are recording',
      message: 'Finish or discard the take first. PadLab will not swap versions underneath a live microphone.',
      timeout: 6000,
    });
    return;
  }
  await saveNow().catch(() => undefined);
  await new Promise<void>((resolve) => {
    const done = (): void => {
      navigator.serviceWorker.removeEventListener('controllerchange', done);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, 3000);
    navigator.serviceWorker.addEventListener('controllerchange', done);
    waiting.postMessage({ type: 'SKIP_WAITING' });
  });
  location.reload();
}

async function main(): Promise<void> {
  const root = document.getElementById('app');
  if (root === null) throw new Error('PadLab could not find its mount point.');

  armAudioUnlock();
  const stopRouter = startRouter();
  createShell(root);

  let lastProjectID: string | null = null;
  try {
    ({ lastProjectID } = await bootstrap());
  } catch (error) {
    reportFailure('PadLab could not open local storage', error);
  }

  // A deep link wins over the restored project: the user asked for that document.
  const requested = launchRoute.projectID ?? null;
  const wanted = requested ?? lastProjectID;

  if (wanted !== null) {
    const opened = await openProject(wanted);
    if (!opened && requested !== null) {
      // The advice and the means to follow it, together. This used to say "Import its backup"
      // and stop there, leaving the user to work out that Import lives on Home and takes a
      // .padlab file — a recovery described but not offered.
      pushBanner({
        kind: 'warning',
        title: 'That link points at a project you do not have',
        message:
          'Boards live on the device that made them, so a link to one only opens there. Ask for its .padlab backup and import that, or pick a project below.',
        actionLabel: 'Import a backup',
        action: () => void importProjectBackup(),
        timeout: 0,
      });
      await navigate({ name: 'home' }, { replace: true });
    }
  }

  await ensureStarterContent();

  // Two first runs, not one. The browser-mode tour explains the instrument; the installed
  // tour — which had a signal, `installedOnboardingPending`, that nothing ever read — covers
  // what changes once PadLab is on the Home Screen.
  if ((!settings.peek().onboardingComplete || installedOnboardingPending.peek()) && launchRoute.name === 'home') {
    await navigate({ name: 'onboarding' }, { replace: true });
  } else if (project.peek() !== null && launchRoute.name === 'home' && requested === null && lastProjectID !== null) {
    // A relaunch returns you to the board you were playing, which is what "resume where I
    // left off" has to mean for an instrument.
    await navigate({ name: 'board', projectID: lastProjectID }, { replace: true });
  }

  // A payload the OS share sheet handed to the service worker. It was written into the
  // `padlab-inbox` cache, redirected here with a token, and then dropped: nothing called
  // `takeSharedPayload`, `clearLaunchQuery()` wiped the token, and the files sat in a cache
  // until the seven-day prune deleted them. Collected *before* the token is cleared.
  const shareToken = launchIntent.shareToken;
  clearLaunchQuery();
  if (shareToken !== null) void collectSharedFiles(shareToken);
  else if (launchIntent.shareFailed) {
    pushBanner({
      kind: 'warning',
      title: 'The shared file did not arrive',
      message: 'PadLab could not read what was shared with it. Try again, or import the file from the Library screen.',
      timeout: 8000,
    });
  }

  // A manifest shortcut is an instruction, not a decoration. All three used to open the
  // restored board because the only consumer of `launchIntent.screen` was a log line.
  await followLaunchShortcut(launchIntent.screen);

  // Lock-screen and headphone controls, where the platform provides them.
  const stopMediaSession = configureMediaSession({
    play: () => void sequencer.play(),
    pause: () => sequencer.stop(),
    stop: () => sequencer.stop(),
  });
  setNowPlaying({ title: project.peek()?.name ?? 'PadLab', subtitle: 'PadLab' });
  setTransportState('paused');

  registerServiceWorker();
  reportRecordingState();

  // The sound library — three hundred generated sounds — is built in the background rather
  // than at boot. Deferred to idle so it never competes with the first paint or with the
  // service worker registration, and staged inside so the first sixteen drums land on the
  // board within a second or two while the rest fills in behind a progress card in the
  // Library tab. Nothing here touches audio output, so a user playing pads over the top of it
  // hears every tap.
  whenIdle(() => ensureLibraryInstalled());

  window.addEventListener('pagehide', () => {
    stopMediaSession();
    stopRouter();
  });

  log.info('PadLab ready', { launch: tag(launchIntent.source), project: project.peek() !== null });
}

void main().catch((error: unknown) => {
  log.error('Boot failed', error);
  document.getElementById('padlab-boot')?.remove();
  const root = document.getElementById('app');
  if (root === null) return;
  const message = error instanceof Error ? error.message : 'PadLab could not start in this browser.';
  const wrapper = document.createElement('div');
  wrapper.className = 'boot-failure safe-all';
  const kicker = document.createElement('p');
  kicker.className = 'kicker';
  kicker.textContent = 'PadLab could not start';
  const heading = document.createElement('h1');
  heading.textContent = 'Something went wrong before the instrument loaded';
  const detail = document.createElement('p');
  detail.textContent = message;
  const hint = document.createElement('p');
  hint.className = 'muted';
  hint.textContent = 'Reload the page. If it keeps happening, PadLab may not have permission to store data in this browser. Private browsing blocks it.';
  wrapper.append(kicker, heading, detail, hint);
  root.replaceChildren(wrapper);
  document.documentElement.dataset['appReady'] = 'true';
});
